import { message } from 'antd'
import { Lead, LeadStage, LeadSource } from '@/types'
import { api, ApiError } from '@/services/api'
import { standardizeCompanyName } from '@/utils/companyMatch'
import { computeLeadDeactiveState } from '@/utils/gst'

const EVENT = 'sp-leads-updated'

// Normalise values coming back from the API: string dates become Date
// objects, and Postgres `numeric` columns — which the `pg` driver returns as
// strings (e.g. "12.00") to avoid float precision loss — become real numbers.
// Without the latter, sanctionedDemand stays a string on an existing lead,
// and LeadForm's `{ type: 'number', min: 1 }` validation rule rejects it
// outright, silently blocking every edit before it ever reaches the server
// (a freshly-created lead doesn't hit this: InputNumber hands the form a
// real number the first time, it's only round-tripping through the API that
// turns it into a string).
const parseLeadDates = (lead: any): Lead => {
  return {
    ...lead,
    companyName: standardizeCompanyName(lead.companyName || ''),
    sanctionedDemand: Number(lead.sanctionedDemand ?? 0),
    requiredUnitsPerAnnum:
      lead.requiredUnitsPerAnnum == null ? lead.requiredUnitsPerAnnum : Number(lead.requiredUnitsPerAnnum),
    ...computeLeadDeactiveState(lead),
    createdAt: new Date(lead.createdAt),
    updatedAt: new Date(lead.updatedAt),
    discussionHistory: (lead.discussionHistory || []).map((disc: any) => ({
      ...disc,
      createdAt: new Date(disc.createdAt),
    })),
  }
}

// ── In-memory cache (synchronous reads) ──────────────────────────────────
// The database is the source of truth. The cache starts empty and is filled
// only by refresh() from the API — no localStorage, no offline seed.
let cache: Lead[] = []
// True once the first refresh() attempt has finished (success or failure) —
// see CrudStore.loaded in utils/createStore.ts for the same pattern.
let hasLoadedOnce = false

const emit = () => window.dispatchEvent(new CustomEvent(EVENT))

// Serialise API writes (create/update/delete) so refresh() can wait for them to
// land before re-fetching. Without this, a list refresh fired right after a
// create races the in-flight POST and overwrites the cache without the new lead
// ("created but not in list" — more likely on slower/production networks).
let writeChain: Promise<unknown> = Promise.resolve()
const enqueueWrite = (run: () => Promise<unknown>): Promise<unknown> => {
  writeChain = writeChain.catch(() => {}).then(run)
  return writeChain
}

export const leadStore = {
  /** Window event fired whenever the lead list changes — subscribe for live UI updates. */
  eventName: EVENT,
  /** True once the first refresh() attempt has finished (success or failure) —
   *  mirrors CrudStore.loaded (utils/createStore.ts). False for the brief
   *  window between mount and that first response. */
  get loaded() { return hasLoadedOnce },
  subscribe(handler: () => void): () => void {
    window.addEventListener(EVENT, handler)
    return () => window.removeEventListener(EVENT, handler)
  },

  // ── Synchronous reads (return the cache) ──────────────────────────────
  getLeads(): Lead[] {
    return cache
  },
  getLead(id: string): Lead | undefined {
    return cache.find((l) => l.id === id)
  },

  // ── Load fresh data from the API into the cache ───────────────────────
  async refresh(): Promise<Lead[]> {
    try {
      // Let any pending create/update/delete finish first so the fetch below
      // reflects them, instead of racing and clobbering optimistic changes.
      await writeChain.catch(() => {})
      const data = await api.get<any[]>('/leads')
      if (Array.isArray(data)) {
        cache = data.map(parseLeadDates)
        emit()
      }
    } catch (err) {
      console.error('API Hydrate Error /leads:', err)
      /* backend unreachable — keep the cached/local data */
    } finally {
      hasLoadedOnce = true
    }
    return cache
  },

  // Builds the post-save Lead object and applies it to the in-memory cache
  // (create or merge-update), shared by the fire-and-forget and
  // confirmation-awaiting save paths below.
  _applyOptimistic(leadData: Partial<Lead> & { id?: string }): { saved: Lead; isUpdate: boolean } {
    const now = new Date()
    let saved: Lead
    const idx = leadData.id ? cache.findIndex((l) => l.id === leadData.id) : -1
    if (idx !== -1) {
      saved = { ...cache[idx], ...leadData, updatedAt: now } as Lead
    } else {
      const newId = leadData.id || `L${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).slice(2, 5).toUpperCase()}`
      saved = {
        id: newId,
        companyName: leadData.companyName || '',
        customerName: leadData.customerName || '',
        address: leadData.address || '',
        city: leadData.city || '',
        pinCode: leadData.pinCode || '',
        contactNumber: leadData.contactNumber || '',
        email: leadData.email || '',
        htscNumber: leadData.htscNumber,
        edcName: leadData.edcName,
        sanctionedDemand: leadData.sanctionedDemand || 0,
        demandUnit: leadData.demandUnit || 'KW',
        drawlVoltage: leadData.drawlVoltage || '',
        typeOfPower: leadData.typeOfPower,
        modeOfSupply: leadData.modeOfSupply,
        requiredUnitsPerAnnum: leadData.requiredUnitsPerAnnum,
        leadType: leadData.leadType || 'Consumer',
        leadSource: leadData.leadSource || 'Other',
        stage: leadData.stage || LeadStage.COLD,
        assignedTo: leadData.assignedTo,
        gstNumber: leadData.gstNumber,
        createdAt: now,
        updatedAt: now,
        discussionHistory: leadData.discussionHistory || [],
      } as Lead
    }

    // Auto-compute deactive mode & 2-day GST/HTSC reminder date — Deactive
    // only kicks in once the grace period has actually elapsed, not the
    // instant HTSC/GST goes missing (see computeLeadDeactiveState).
    Object.assign(saved, computeLeadDeactiveState(saved))

    if (idx !== -1) {
      cache = cache.map((l) => (l.id === saved.id ? saved : l))
    } else {
      cache = [saved, ...cache]
    }
    emit()
    return { saved, isUpdate: idx !== -1 }
  },

  // ── Create or update (optimistic cache + write-through to API) ────────
  // Fire-and-forget: returns as soon as the local cache is updated. Good for
  // background writes (adding a note, changing a stage) where the caller
  // doesn't need to confirm the server accepted it before moving on.
  saveLead(leadData: Partial<Lead> & { id?: string }): Lead {
    const previous = cache
    const { saved, isUpdate } = this._applyOptimistic(leadData)
    // write-through (queued so refresh() waits for it; ignored if offline).
    // An existing id must go through PUT — POSTing it again hits the create
    // endpoint's own-id collision check and 409s instead of applying the edit.
    void enqueueWrite(() =>
      (isUpdate ? api.put(`/leads/${saved.id}`, saved) : api.post('/leads', saved)).catch((err) => {
        console.error('[leadStore] Failed to save to the server:', err)
        // Roll back the optimistic change — it was never actually saved — and
        // surface the real reason (e.g. a 409 duplicate-name rejection) instead
        // of leaving the UI showing a change the database never received.
        cache = previous
        emit()
        if (err instanceof ApiError && err.status && err.status < 500 && err.message) {
          message.error(err.message)
        } else {
          message.error("Couldn't save this lead to the server — check your connection and try again.")
        }
      })
    )
    return saved
  },

  // Same optimistic cache update as saveLead(), but resolves only once the
  // write-through to the API has actually landed, and rejects (with the real
  // ApiError, rolling the cache back) if it didn't. For flows like the Edit
  // Lead form that must not tell the user "saved" — and navigate away —
  // before the server has actually agreed to the change.
  async saveLeadAndConfirm(leadData: Partial<Lead> & { id?: string }): Promise<Lead> {
    const previous = cache
    const { saved, isUpdate } = this._applyOptimistic(leadData)
    try {
      await enqueueWrite(() => (isUpdate ? api.put(`/leads/${saved.id}`, saved) : api.post('/leads', saved)))
      return saved
    } catch (err) {
      cache = previous
      emit()
      throw err
    }
  },

  saveLeads(leads: Lead[]): void {
    cache = leads
    emit()
  },

  // Delete on the server first, then locally. We optimistically remove the lead
  // for a snappy UI, but if the server rejects (e.g. expired/invalid token) we
  // roll back and throw so the caller can show a real error — instead of the old
  // behaviour where the lead silently reappeared on the next refresh.
  async deleteLead(id: string): Promise<void> {
    const prev = cache
    cache = cache.filter((l) => l.id !== id)
    emit()
    try {
      await enqueueWrite(() => api.del('/leads/' + id))
    } catch (err) {
      cache = prev // restore — the delete didn't actually happen
      emit()
      throw err
    }
  },
}
